/**
 * Persona Workshop draft-editing Zustand store.
 *
 * Hybrid-state shape (T1):
 *   - serverBundle: last-known canonical bundle from RQ (or null if none yet)
 *   - draftBundle:  in-flight overlay; populated by typed editing methods
 *   - dirtyPaths:   string paths into the bundle that have unsaved edits
 *
 * T2 materializes the editing primitives:
 *   - `updateBlock(category, content)` — typed editing method. Card
 *     components (P4.T3/T4/T5) will add their own typed methods
 *     (`addSkill`, `bindTool`, `installWorkflow`, etc.) following the
 *     same pattern; each method computes a derived `block:<cat>...`
 *     style string path and adds it to dirtyPaths for the
 *     Workshop top-bar Save visualization.
 *   - `commitDraft()` — calls a module-level registered mutation
 *     (PUT + activate chained, registered by the Workshop page's
 *     mount effect via `registerComposerMutation`). On success, clears
 *     draft state + updates serverBundle. On rejection, leaves
 *     draft state untouched so the user can retry.
 *
 * P4.T6 retired `applyEdit` entirely. The typed-action surface
 * (updateBlock, addSkill/removeSkill, bindTool/unbindTool,
 * installWorkflow/removeWorkflow/setWorkflowChannel) plus the new
 * `replaceDraft(bundle)` together cover every editing affordance the
 * Workshop needs in v1 — no consumer ever needed generic path-edits.
 * The Power Editor (Workshop Advanced) stages a hand-edited JSON bundle
 * via replaceDraft, with a single power-editor.dirty marker.
 *
 * `BlockCategory` mirrors the LLM-known vocab in
 * `backend/app/services/prompt_ai_assistant.py` (line 24) plus the new
 * `tone` entry added in T2. Until both surfaces share a JSON manifest
 * (Phase 5+ consolidation), keep them in sync by hand.
 *
 * Typed action methods land per card-ticket:
 *   - T2: `updateBlock(category, content)` — block:<cat>.content marker.
 *   - T3: `addSkill(name, mode)` + `removeSkill(name)` — single
 *     `skill:<name>.changed` marker derived from state comparison.
 *   - T4: `bindTool(capability_id, adapter_id)` +
 *     `unbindTool(capability_id)` — single `binding:<cap>.changed`
 *     marker derived from `bindingsEqual` structural comparison
 *     (`policy_overrides` excluded).
 *   - T5 (planned): `installWorkflow` / `removeWorkflow`.
 *
 * `CommitMutationArgs.updates` widens additively per card-ticket
 * (T2 = blocks; T3 = + default_skills + auto_skills; T4 = +
 * tool_bindings; T5 = + workflow_bindings). `commitDraft` sends the
 * full slice on every Save — PersonaBundleUpdate preserves unset
 * fields from the prior version (P1.T1 contract).
 *
 * See:
 *   - plan §4 P4.T2 / P4.T3 / P4.T4 deliverable blocks
 *   - plan §5 design pins 2026-05-21 (P4.T2 typed-action / tone /
 *     mutation-injection; P4.T3 dirty-marker derivation; P4.T4
 *     bindingsEqual + policy_overrides exclusion)
 */
import { create } from 'zustand'
import type { ApiPersona } from '@/types/persona'
import type {
  PersonaBundleBlock,
  PersonaBundleResponse,
} from '@/types/personaWizard'

const NO_MUTATION_REGISTERED =
  'composerStore.commitDraft called before registerComposerMutation — the Workshop page (PersonaWorkshop/index.tsx) must register the useUpdatePersonaBundle mutation in a useEffect on mount.'

/**
 * Mirrors backend/app/services/prompt_ai_assistant.py:24 vocab plus the
 * new `tone` entry. Until both surfaces share a JSON manifest, keep
 * them in sync by hand. Adding a new category is additive — the bundle
 * schema's `category: str` accepts anything.
 */
export type BlockCategory =
  | 'identity'
  | 'trait'
  | 'tone'
  | 'behavioral'
  | 'constraints'
  | 'domain'
  | 'output_format'
  | 'examples'
  | 'freeform'
  | 'never_forget'
  | 'escalation'
  | 'do_examples'
  | 'dont_examples'

/**
 * Skill installation mode (P4.T3).
 *
 * - 'default' — bundle.default_skills: always loaded into the persona's context.
 * - 'auto'    — bundle.auto_skills: loaded when the intent classifier matches.
 *               (Classifier wiring is Phase 2/3 work; installed auto-load
 *               skills are inert until that lands — section header in the
 *               Skills card is honest about this.)
 */
export type SkillMode = 'default' | 'auto'

/**
 * Tool binding value (P4.T4). Mirrors the server-side `ToolBindingValue`
 * shape in `backend/app/routers/persona_tool_bindings.py:54-64` and the
 * spec §6.7 `tool_bindings: {capability_id: {adapter_id, config_id}}`
 * shape (`credential_set_id` is the synonym for `config_id`).
 *
 * `policy_overrides` is power-user surface — the Power Editor drawer
 * (P4.T6) + the admin surface own its editing. T4's typed `bindTool`
 * preserves the field across rebind so a Power-Editor-set value never
 * disappears on a subsequent Workshop Save.
 */
export interface ToolBindingValue {
  adapter_id: string
  credential_set_id?: string | null
  policy_overrides?: Record<string, unknown>
}

/**
 * Workflow channel selector (P4.T5). Mirrors `workflow_routing.py:79-84`
 * `ChannelKind` Literal plus the `"*"` wildcard. The picker installs
 * with `"*"` by default (most permissive); user narrows via the
 * inline Select on the installed row.
 */
export type WorkflowChannel = '*' | 'whatsapp' | 'chat' | 'voice'

/**
 * Workflow binding value (P4.T5). Mirrors the persisted shape on
 * `PersonaBundle.workflow_bindings` (a list of these dicts; see
 * `backend/app/services/workflow_routing.py:28-46`).
 *
 * The index signature accommodates future fields (per
 * `workflow_routing.py:50` — `autostart_on_intent` is a Phase 4+
 * forward-promise). T5's typed `setWorkflowChannel` spread-preserves
 * unknown fields so a future Power-Editor-set value survives a
 * subsequent Workshop channel change.
 */
export interface WorkflowBindingValue {
  workflow_id: string
  autostart_on_channel: WorkflowChannel
  [key: string]: unknown
}

/**
 * Lookup helper: returns the mode a skill is installed under in the
 * bundle, or null if not installed in either list. Used by addSkill /
 * removeSkill to derive the `skill:<name>.changed` dirty marker by
 * comparing draft vs server state (plan §4 P4.T3 dirty-marker design
 * pin). The single derived marker correctly handles all six edit
 * sequences including the tricky remove-from-default → add-to-auto
 * mode-change case that naive .installed/.removed bookkeeping
 * mishandles.
 */
function getInstalledMode(
  bundle: PersonaBundleResponse | null,
  name: string,
): SkillMode | null {
  if (!bundle) return null
  if (bundle.default_skills.some((s) => s.name === name)) return 'default'
  if (bundle.auto_skills.some((s) => s.name === name)) return 'auto'
  return null
}

/**
 * Structural equality used for the `binding:<capability_id>.changed`
 * dirty-marker derivation (P4.T4). Compares `adapter_id` + normalized
 * `credential_set_id` (null vs undefined collapsed via `?? null`).
 *
 * **`policy_overrides` intentionally excluded.** Two correctness pins:
 * (a) `bindTool` preserves existing `policy_overrides` on rebind, so
 * the field is byte-equal pre/post by construction — including it
 * would never matter for T4-driven edits; (b) if the serverBundle is
 * rehydrated after an orchestrator-set `policy_overrides` write,
 * including the dict in the equality check could falsely flag the
 * binding dirty when the dict reference differs from the prior fetch.
 * The exclusion keeps the marker honest about what T4 actually edits.
 *
 * Pinned in plan §5 (2026-05-21) decision-log entry "P4.T4: dirty
 * marker = state-comparison on binding value structural equality,
 * `policy_overrides` EXCLUDED from comparison".
 */
function bindingsEqual(
  a: ToolBindingValue | undefined,
  b: ToolBindingValue | undefined,
): boolean {
  if (!a && !b) return true
  if (!a || !b) return false
  if (a.adapter_id !== b.adapter_id) return false
  if ((a.credential_set_id ?? null) !== (b.credential_set_id ?? null)) return false
  return true
}

/**
 * Lookup helper: returns the workflow binding entry from the bundle's
 * `workflow_bindings` list matching `workflow_id`, or undefined if not
 * installed. Used by `installWorkflow` (no-op-on-duplicate guard) +
 * `setWorkflowChannel` (entry-replacement target) + dirty-marker
 * recomputation. P4.T5.
 */
function findWorkflowBinding(
  bundle: PersonaBundleResponse | null,
  workflow_id: string,
): WorkflowBindingValue | undefined {
  if (!bundle) return undefined
  const list = bundle.workflow_bindings as WorkflowBindingValue[] | undefined
  if (!list) return undefined
  return list.find((b) => b.workflow_id === workflow_id)
}

/**
 * Structural equality used for the `workflow:<workflow_id>.changed`
 * dirty-marker derivation (P4.T5). Compares `workflow_id` +
 * `autostart_on_channel`.
 *
 * **No fields excluded today.** Unlike T4's `bindingsEqual` (which
 * excludes `policy_overrides`), T5's binding shape has no power-user-
 * only fields. **Forward-promise contract**: when `autostart_on_intent`
 * lands as a power-user-only field per `workflow_routing.py:50` Phase
 * 4+ forward-promise, this helper SHOULD evolve to exclude it from
 * comparison — mirroring T4's `policy_overrides` exclusion. T5's typed
 * methods today use spread-preserve-unknown-fields in `setWorkflowChannel`
 * so future fields persist across mutations once added.
 *
 * Pinned in plan §5 (2026-05-21) decision-log entry "P4.T5: dirty
 * marker = state-comparison via `workflowBindingsEqual` structural
 * equality".
 */
function workflowBindingsEqual(
  a: WorkflowBindingValue | undefined,
  b: WorkflowBindingValue | undefined,
): boolean {
  if (!a && !b) return true
  if (!a || !b) return false
  if (a.workflow_id !== b.workflow_id) return false
  if (a.autostart_on_channel !== b.autostart_on_channel) return false
  return true
}

export interface CommitMutationArgs {
  bundleId: string
  // P4.T2 shipped `{ blocks }` only. P4.T3 widened to include
  // `default_skills` + `auto_skills`. P4.T4 widened to include
  // `tool_bindings`. P4.T5 widens further to include
  // `workflow_bindings` — fourth (and final card-ticket) additive
  // widening. All fields optional — PersonaBundleUpdate accepts any
  // subset and preserves unset fields from the prior version (P1.T1
  // contract).
  updates: {
    blocks?: PersonaBundleBlock[]
    default_skills?: { name: string }[]
    auto_skills?: { name: string }[]
    tool_bindings?: Record<string, unknown>
    workflow_bindings?: unknown[]
  }
}

export type CommitMutation = (
  args: CommitMutationArgs,
) => Promise<PersonaBundleResponse>

/**
 * Module-level mutation slot. Zustand stores are not React hooks and
 * can't call `useMutation` directly; the Workshop page's mount effect
 * calls `registerComposerMutation(useUpdatePersonaBundle().mutateAsync)`
 * on mount and `registerComposerMutation(null)` on unmount.
 */
let _commitMutation: CommitMutation | null = null

export function registerComposerMutation(fn: CommitMutation | null): void {
  _commitMutation = fn
}

interface ComposerState {
  personaId: string | null
  persona: ApiPersona | null
  serverBundle: PersonaBundleResponse | null
  draftBundle: PersonaBundleResponse | null
  dirtyPaths: ReadonlySet<string>
  load: (persona: ApiPersona, bundle: PersonaBundleResponse | null) => void
  updateBlock: (category: BlockCategory, content: string) => void
  updateBlockAt: (index: number, content: string) => void
  revertBlockAt: (index: number) => void
  addBlock: (category: BlockCategory | (string & {})) => void
  /** SA-31: append template starter blocks (content included) to the
   * draft, in declared order — the Workshop's install-offer accept. */
  installStarterBlocks: (
    blocks: ReadonlyArray<{ category: string; content: string }>,
  ) => void
  removeBlockAt: (index: number) => void
  moveBlock: (from: number, to: number) => void
  addSkill: (name: string, mode: SkillMode) => void
  removeSkill: (name: string) => void
  bindTool: (capability_id: string, adapter_id: string) => void
  unbindTool: (capability_id: string) => void
  installWorkflow: (workflow_id: string) => void
  removeWorkflow: (workflow_id: string) => void
  setWorkflowChannel: (workflow_id: string, channel: WorkflowChannel) => void
  replaceDraft: (bundle: PersonaBundleResponse) => void
  commitDraft: () => Promise<void>
  discardDraft: () => void
  reset: () => void
}

const INITIAL = {
  personaId: null,
  persona: null,
  serverBundle: null,
  draftBundle: null,
  dirtyPaths: new Set<string>() as ReadonlySet<string>,
}

export const useComposerStore = create<ComposerState>((set, get) => ({
  ...INITIAL,
  load: (persona, bundle) =>
    set({
      personaId: persona.id,
      persona,
      serverBundle: bundle,
      draftBundle: null,
      dirtyPaths: new Set<string>(),
    }),
  updateBlock: (category, content) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      // Defensive: card should never call updateBlock before load().
      // Throw rather than silently no-op so a wiring bug surfaces.
      throw new Error(
        'composerStore.updateBlock called before load() — no bundle to edit.',
      )
    }
    const existingIdx = base.blocks.findIndex((b) => b.category === category)
    const nextBlocks =
      existingIdx === -1
        ? [...base.blocks, { category, content }]
        : base.blocks.map((b, i) =>
            i === existingIdx ? { ...b, content } : b,
          )
    const nextPaths = new Set(dirtyPaths)
    nextPaths.add(`block:${category}.content`)
    set({
      draftBundle: { ...base, blocks: nextBlocks },
      dirtyPaths: nextPaths,
    })
  },
  updateBlockAt: (index, content) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.updateBlockAt called before load() — no bundle to edit.',
      )
    }
    if (index < 0 || index >= base.blocks.length) return
    const nextBlocks = base.blocks.map((b, i) =>
      i === index ? { ...b, content } : b,
    )
    const nextPaths = new Set(dirtyPaths)
    nextPaths.add(`block:${index}.content`)
    set({ draftBundle: { ...base, blocks: nextBlocks }, dirtyPaths: nextPaths })
  },
  addBlock: (category) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.addBlock called before load() — no bundle to edit.',
      )
    }
    const nextBlocks = [...base.blocks, { category, content: '' }]
    const nextPaths = new Set(dirtyPaths)
    nextPaths.add(`block:${category}.added`)
    set({ draftBundle: { ...base, blocks: nextBlocks }, dirtyPaths: nextPaths })
  },
  installStarterBlocks: (blocks) => {
    if (blocks.length === 0) return
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.installStarterBlocks called before load() — no bundle to edit.',
      )
    }
    const nextBlocks = [
      ...base.blocks,
      ...blocks.map(({ category, content }) => ({ category, content })),
    ]
    const nextPaths = new Set(dirtyPaths)
    for (const { category } of blocks) nextPaths.add(`block:${category}.added`)
    set({ draftBundle: { ...base, blocks: nextBlocks }, dirtyPaths: nextPaths })
  },
  removeBlockAt: (index) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.removeBlockAt called before load() — no bundle to edit.',
      )
    }
    if (index < 0 || index >= base.blocks.length) return
    const nextBlocks = base.blocks.filter((_, i) => i !== index)
    const nextPaths = new Set(dirtyPaths)
    nextPaths.add(`block:${index}.removed`)
    set({ draftBundle: { ...base, blocks: nextBlocks }, dirtyPaths: nextPaths })
  },
  moveBlock: (from, to) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) return
    const n = base.blocks.length
    if (from < 0 || from >= n || to < 0 || to >= n || from === to) return
    const nextBlocks = [...base.blocks]
    const [moved] = nextBlocks.splice(from, 1)
    nextBlocks.splice(to, 0, moved)
    const nextPaths = new Set(dirtyPaths)
    nextPaths.add('block:reorder')
    set({ draftBundle: { ...base, blocks: nextBlocks }, dirtyPaths: nextPaths })
  },
  revertBlockAt: (index) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    // Nothing to revert without both a draft and a saved baseline.
    if (draftBundle === null || serverBundle === null) return
    const live = draftBundle.blocks[index]
    if (!live) return
    // (category, ordinal) identity — same rule as focusTargetAcrossSnapshots:
    // index-based lookup would grab the wrong block after draft inserts/moves.
    const ordinal = draftBundle.blocks
      .slice(0, index)
      .filter((b) => b.category === live.category).length
    // Reorder-ambiguity guard: when the ONLY dirt is a whole-list reorder
    // and this block's category repeats in the draft, (category, ordinal)
    // pairs each block with its POSITIONAL counterpart in serverBundle —
    // for a same-category pair that swapped positions that's the WRONG
    // baseline (it would silently copy the sibling's content onto this
    // block). Only ambiguous when the category has more than one instance;
    // a single-instance category still reverts correctly after a reorder.
    if (
      dirtyPaths.has('block:reorder') &&
      draftBundle.blocks.filter((b) => b.category === live.category).length > 1
    ) {
      return
    }
    let seen = 0
    let baseline: string | null = null
    for (const b of serverBundle.blocks) {
      if (b.category !== live.category) continue
      if (seen === ordinal) {
        baseline = b.content
        break
      }
      seen++
    }
    if (baseline === null) return // draft-added block — nothing saved to revert to
    const nextBlocks = draftBundle.blocks.map((b, i) =>
      i === index ? { ...b, content: baseline } : b,
    )
    const nextPaths = new Set(dirtyPaths)
    nextPaths.delete(`block:${index}.content`)
    // The category-keyed marker is only ever written by `updateBlock`,
    // which always targets the FIRST (ordinal-0) block of a category (the
    // chat-coach Apply path in applyCoachProposal.ts). Deleting it for a
    // same-category SIBLING (ordinal >= 1) would erase dirt that belongs
    // to a different block — scope the deletion to ordinal 0.
    if (ordinal === 0) {
      nextPaths.delete(`block:${live.category}.content`)
    }
    if (nextPaths.size === 0) {
      // Every marker was this block's — the draft is the baseline again.
      set({ draftBundle: null, dirtyPaths: nextPaths })
    } else {
      set({ draftBundle: { ...draftBundle, blocks: nextBlocks }, dirtyPaths: nextPaths })
    }
  },
  addSkill: (name, mode) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.addSkill called before load() — no bundle to edit.',
      )
    }
    // Defensive no-op if already installed in either list. UI should
    // prevent this (picker filters already-installed) but the guard
    // keeps the store honest against double-install bugs.
    if (getInstalledMode(base, name) !== null) return
    const newEntry = { name }
    const next: PersonaBundleResponse =
      mode === 'default'
        ? { ...base, default_skills: [...base.default_skills, newEntry] }
        : { ...base, auto_skills: [...base.auto_skills, newEntry] }
    const draftMode = getInstalledMode(next, name)
    const serverMode = getInstalledMode(serverBundle, name)
    const nextPaths = new Set(dirtyPaths)
    const marker = `skill:${name}.changed`
    if (draftMode !== serverMode) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  removeSkill: (name) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.removeSkill called before load() — no bundle to edit.',
      )
    }
    if (getInstalledMode(base, name) === null) return
    const next: PersonaBundleResponse = {
      ...base,
      default_skills: base.default_skills.filter((s) => s.name !== name),
      auto_skills: base.auto_skills.filter((s) => s.name !== name),
    }
    const draftMode = getInstalledMode(next, name)
    const serverMode = getInstalledMode(serverBundle, name)
    const nextPaths = new Set(dirtyPaths)
    const marker = `skill:${name}.changed`
    if (draftMode !== serverMode) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  bindTool: (capability_id, adapter_id) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.bindTool called before load() — no bundle to edit.',
      )
    }
    const existing = base.tool_bindings[capability_id] as
      | ToolBindingValue
      | undefined
    if (existing && existing.adapter_id === adapter_id) {
      // Same adapter — defensive no-op (preserves credential_set_id +
      // policy_overrides intact). The picker UI shouldn't enable this
      // click but the guard prevents redundant dirty-mark flicker.
      return
    }
    const newValue: ToolBindingValue =
      existing && existing.policy_overrides
        ? { adapter_id, policy_overrides: existing.policy_overrides }
        : { adapter_id }
    // Note: `credential_set_id` is deliberately NOT carried over on
    // rebind — creds were scoped to the old adapter; resolver falls
    // back to the workspace default for the new adapter. Pinned in
    // plan §5 (2026-05-21).
    const nextBindings = {
      ...base.tool_bindings,
      [capability_id]: newValue,
    }
    const next: PersonaBundleResponse = {
      ...base,
      tool_bindings: nextBindings,
    }
    const serverBinding = serverBundle?.tool_bindings[capability_id] as
      | ToolBindingValue
      | undefined
    const nextPaths = new Set(dirtyPaths)
    const marker = `binding:${capability_id}.changed`
    if (!bindingsEqual(newValue, serverBinding)) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  unbindTool: (capability_id) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.unbindTool called before load() — no bundle to edit.',
      )
    }
    if (base.tool_bindings[capability_id] === undefined) return
    const nextBindings = { ...base.tool_bindings }
    delete nextBindings[capability_id]
    const next: PersonaBundleResponse = {
      ...base,
      tool_bindings: nextBindings,
    }
    const serverBinding = serverBundle?.tool_bindings[capability_id] as
      | ToolBindingValue
      | undefined
    const nextPaths = new Set(dirtyPaths)
    const marker = `binding:${capability_id}.changed`
    if (!bindingsEqual(undefined, serverBinding)) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  installWorkflow: (workflow_id) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.installWorkflow called before load() — no bundle to edit.',
      )
    }
    // Defensive no-op if already installed. Picker should filter
    // already-installed workflows but the guard keeps the store honest.
    if (findWorkflowBinding(base, workflow_id) !== undefined) return
    const newEntry: WorkflowBindingValue = {
      workflow_id,
      autostart_on_channel: '*',
    }
    const baseList = (base.workflow_bindings ?? []) as WorkflowBindingValue[]
    const nextList = [...baseList, newEntry]
    const next: PersonaBundleResponse = {
      ...base,
      workflow_bindings: nextList,
    }
    const serverEntry = findWorkflowBinding(serverBundle, workflow_id)
    const nextPaths = new Set(dirtyPaths)
    const marker = `workflow:${workflow_id}.changed`
    if (!workflowBindingsEqual(newEntry, serverEntry)) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  removeWorkflow: (workflow_id) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.removeWorkflow called before load() — no bundle to edit.',
      )
    }
    if (findWorkflowBinding(base, workflow_id) === undefined) return
    const baseList = (base.workflow_bindings ?? []) as WorkflowBindingValue[]
    const nextList = baseList.filter((b) => b.workflow_id !== workflow_id)
    const next: PersonaBundleResponse = {
      ...base,
      workflow_bindings: nextList,
    }
    const serverEntry = findWorkflowBinding(serverBundle, workflow_id)
    const nextPaths = new Set(dirtyPaths)
    const marker = `workflow:${workflow_id}.changed`
    if (!workflowBindingsEqual(undefined, serverEntry)) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  setWorkflowChannel: (workflow_id, channel) => {
    const { draftBundle, serverBundle, dirtyPaths } = get()
    const base = draftBundle ?? serverBundle
    if (base === null) {
      throw new Error(
        'composerStore.setWorkflowChannel called before load() — no bundle to edit.',
      )
    }
    const existing = findWorkflowBinding(base, workflow_id)
    if (existing === undefined) return  // no-op if not installed
    // Same-channel no-op — defensive guard (Select shouldn't fire
    // onChange with the same value, but mirrors T4's bindTool
    // same-adapter no-op for parity + prevents redundant dirty-mark
    // flicker). M3 review-fix pin.
    if (existing.autostart_on_channel === channel) return
    // Spread-preserve-unknown-fields — forward-compat for
    // `autostart_on_intent` (per workflow_routing.py:50 Phase 4+
    // forward-promise) + any other future power-user fields.
    const newEntry: WorkflowBindingValue = {
      ...existing,
      autostart_on_channel: channel,
    }
    const baseList = (base.workflow_bindings ?? []) as WorkflowBindingValue[]
    const nextList = baseList.map((b) =>
      b.workflow_id === workflow_id ? newEntry : b,
    )
    const next: PersonaBundleResponse = {
      ...base,
      workflow_bindings: nextList,
    }
    const serverEntry = findWorkflowBinding(serverBundle, workflow_id)
    const nextPaths = new Set(dirtyPaths)
    const marker = `workflow:${workflow_id}.changed`
    if (!workflowBindingsEqual(newEntry, serverEntry)) nextPaths.add(marker)
    else nextPaths.delete(marker)
    set({ draftBundle: next, dirtyPaths: nextPaths })
  },
  replaceDraft: (bundle: PersonaBundleResponse) => {
    const { serverBundle } = get()
    if (serverBundle === null) {
      // Defensive: the Power Editor drawer is only reachable from a
      // Workshop page that has already loaded a bundle, so a null
      // serverBundle is a wiring bug.
      throw new Error(
        'composerStore.replaceDraft called before load() — no bundle to replace.',
      )
    }
    // Structured clone so subsequent edits on the input from the
    // drawer caller can't bleed into draftBundle.
    const cloned = JSON.parse(JSON.stringify(bundle)) as PersonaBundleResponse
    set({
      draftBundle: cloned,
      dirtyPaths: new Set<string>(['power-editor.dirty']),
    })
  },
  commitDraft: async () => {
    const { draftBundle, serverBundle } = get()
    if (draftBundle === null) return
    if (_commitMutation === null) {
      throw new Error(NO_MUTATION_REGISTERED)
    }
    if (serverBundle === null) {
      // Defensive: draftBundle without serverBundle is impossible via
      // public API (updateBlock / addSkill / removeSkill require a base),
      // but guard anyway.
      throw new Error(
        'composerStore.commitDraft has draftBundle but no serverBundle — load() first.',
      )
    }
    // Payload widens additively per card-ticket: T2 sent { blocks }
    // only; T3 added default_skills + auto_skills; T4 added
    // tool_bindings; T5 adds workflow_bindings — fourth and final
    // card-ticket widening. PersonaBundleUpdate preserves unset
    // fields from the prior version (P1.T1 contract), so sending
    // the full slice on every Save is safe and idempotent.
    const activated = await _commitMutation({
      bundleId: serverBundle.id,
      updates: {
        blocks: draftBundle.blocks,
        default_skills: draftBundle.default_skills,
        auto_skills: draftBundle.auto_skills,
        tool_bindings: draftBundle.tool_bindings,
        workflow_bindings: draftBundle.workflow_bindings,
      },
    })
    set({
      draftBundle: null,
      dirtyPaths: new Set<string>(),
      serverBundle: activated,
    })
  },
  discardDraft: () =>
    set({ draftBundle: null, dirtyPaths: new Set<string>() }),
  reset: () => set(INITIAL),
}))
